// useWeeklyProgress.js - Data hook for the Weekly Production Progress modal
// Place at: src/hooks/useWeeklyProgress.js
//
// ─── Responsibilities ─────────────────────────────────────────────────────────
//
//  1. Calls fetchWeeklyProgress() from dataReceiver once on mount.
//  2. Maps the four backend metric objects onto the `actual` / `goal` fields
//     that weeklyProgress.js expects inside WEEKLY_METRICS:
//
//     Backend field              → WEEKLY_METRICS entry   unit
//     ─────────────────────────────────────────────────────────
//     weeklyUnitsCompleted       → id: "units"            "units"
//     weeklySqftCompleted        → id: "sqft"             "sqft"
//     onTimeCompletion           → id: "ontime"           "%"
//     weeklyQualityPassRate      → id: "quality"          "%"
//
//     Rate metrics (ontime, quality): backend `actual` is a 0–1 decimal ratio.
//     The component's pctOf() helper works on raw actual/goal values and the
//     display value is formatted with fmtValue(actual, "%") → `${actual}%`.
//     Therefore actual and target are converted to percentage points (×100)
//     so the component renders "88.5%" instead of "0.885%".
//
//  3. Exposes { metrics, loading, error, weekStart, weekEnd, refetch } so the
//     caller can pass the live `metrics` array in place of the static
//     WEEKLY_METRICS constant in weeklyProgress.js.
//
// ─── Usage in weeklyProgress.js ──────────────────────────────────────────────
//
//   import { useWeeklyProgress } from "../../../../hooks/useWeeklyProgress";
//
//   const WeeklyProgress = () => {
//     const { metrics, loading, error, weekStart, weekEnd } = useWeeklyProgress();
//     // Replace WEEKLY_METRICS with `metrics` in JSX; render loading/error states.
//   };
//
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import {
  DataReceiverError,
  fetchWeeklyProgress,
} from "../../utils/dataReceiver";

// ─── Static metric config ─────────────────────────────────────────────────────
//
// Icon renderers, labels, sublabels, units, and modalTarget are purely
// presentational - they never change and don't come from the backend.
// We keep them here as the single source of truth and merge live actual/goal
// values on top so weeklyProgress.js can consume a drop-in replacement for
// its WEEKLY_METRICS constant without restructuring any of its render logic.

const METRIC_CONFIG = [
  {
    id: "units",
    label: "Weekly Units Completed",
    sublabel: "Daily Modules",
    unit: "units",
    modalTarget: "dailyModules",
    // Picks weeklyUnitsCompleted from the API response
    apiKey: "weeklyUnitsCompleted",
    // Raw count - no unit conversion needed
    toDisplay: (v) => v,
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <rect x="2" y="3" width="8" height="8" rx="1.5" />
        <rect x="14" y="3" width="8" height="8" rx="1.5" />
        <rect x="2" y="13" width="8" height="8" rx="1.5" />
        <rect x="14" y="13" width="8" height="8" rx="1.5" />
      </svg>
    ),
  },
  {
    id: "sqft",
    label: "SQFT Completed",
    sublabel: "Weekly Square Footage",
    unit: "sqft",
    modalTarget: "weeklySqft",
    apiKey: "weeklySqftCompleted",
    // Raw count - no unit conversion needed
    toDisplay: (v) => v,
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <path d="M3 9L12 3l9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: "ontime",
    label: "On Time Completion",
    sublabel: "Schedule Variance",
    unit: "%",
    modalTarget: "completionVariance",
    apiKey: "onTimeCompletion",
    // Backend returns a 0–1 ratio; component expects percentage points
    toDisplay: (v) => Math.round(v * 1000) / 10, // 0.885 → 88.5
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16.5 14.5" />
      </svg>
    ),
  },
  {
    id: "quality",
    label: "Quality Pass Rate",
    sublabel: "First Pass - Utilities",
    unit: "%",
    modalTarget: "firstPassRate",
    apiKey: "weeklyQualityPassRate",
    // Backend returns a 0–1 ratio; component expects percentage points
    toDisplay: (v) => Math.round(v * 1000) / 10, // 0.92 → 92.0
    icon: (color) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.75"
        style={{ width: 22, height: 22 }}
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
      </svg>
    ),
  },
];

// ─── Mapping helper ───────────────────────────────────────────────────────────

/**
 * Merge a raw API response from fetchWeeklyProgress() with the static
 * METRIC_CONFIG entries to produce the array shape that weeklyProgress.js
 * renders as WEEKLY_METRICS.
 *
 * @param {object} apiData  Normalised response from fetchWeeklyProgress()
 * @returns {Array}         Array of metric objects ready for MetricCard
 */
function buildMetrics(apiData) {
  return METRIC_CONFIG.map((cfg) => {
    const raw = apiData[cfg.apiKey] ?? { actual: 0, target: 0 };
    return {
      // Static presentational fields
      id: cfg.id,
      label: cfg.label,
      sublabel: cfg.sublabel,
      unit: cfg.unit,
      modalTarget: cfg.modalTarget,
      icon: cfg.icon,
      // Live data - converted to the unit the component expects
      actual: cfg.toDisplay(raw.actual),
      goal: cfg.toDisplay(raw.target),
    };
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetch and return live weekly production progress data shaped for
 * weeklyProgress.js.
 *
 * @returns {{
 *   metrics:   Array,           - Drop-in replacement for WEEKLY_METRICS
 *   loading:   boolean,         - True while the first fetch is in-flight
 *   error:     string | null,   - Human-readable error message, or null
 *   weekStart: string | null,   - "YYYY-MM-DD" of current business-week Monday
 *   weekEnd:   string | null,   - "YYYY-MM-DD" of current business-week Friday
 *   refetch:   () => void,      - Manually re-trigger the fetch (e.g. on retry)
 * }}
 */
export function useWeeklyProgress() {
  const [metrics, setMetrics] = useState(() => buildMetrics({}));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);

  // ── Strict-Mode / double-invoke guard ────────────────────────────────────
  //
  // hasFetched ensures at most one in-flight request per mounted instance.
  // Pattern matches onlineMods.js: the ref survives React 18's
  // mount→unmount→remount cycle; the second invocation returns early and the
  // first fetch resolves freely without needing AbortController cleanup
  // (which would cancel the request before it settles and leave the UI stuck
  // in the loading state).
  const hasFetched = useRef(false);

  // ── Core fetch logic - extracted so refetch() can call it too ────────────
  const doFetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchWeeklyProgress();
      setMetrics(buildMetrics(data));
      setWeekStart(data.meta?.weekStart ?? null);
      setWeekEnd(data.meta?.weekEnd ?? null);
    } catch (err) {
      // AbortError = intentional cancellation; not an error worth surfacing
      if (err.name === "AbortError") return;

      setError(
        err instanceof DataReceiverError
          ? err.message
          : "Unexpected error loading weekly progress data.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    doFetch();
  }, [doFetch]);

  // refetch resets the guard so the caller can re-trigger after an error
  const refetch = useCallback(() => {
    hasFetched.current = false;
    doFetch();
  }, [doFetch]);

  return { metrics, loading, error, weekStart, weekEnd, refetch };
}
